import CheckIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RevertIcon from "@mui/icons-material/ReplayRounded";
import { Button } from "@mui/material";
import {
  gridRowSelectionStateSelector,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import type { ReactNode } from "react";
import { useState } from "react";
import { getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ObjectFieldValues } from "../../../../api/types/objectTypes";
import ObjectsListContext from "../../../entityFields/grid/ObjectsListContext";
import InvoiceActionDialog from "./InvoiceActionDialog";
import type { InvoiceAction } from "./invoiceGridActions";
import { isInvoiceActionAvailable } from "./invoiceGridActions";
import InvoicesGridContext from "./InvoicesGridContext";
import useInvoiceDocumentsDownload from "./useInvoiceDocumentsDownload";

interface ActionButton {
  action: InvoiceAction;
  label: string;
  icon: ReactNode;
  color: "primary" | "secondary" | "success" | "error";
}

const actionButtons: ActionButton[] = [
  { action: "SubmitInvoiceForApproval", label: "Submit for Approval", icon: <CheckIcon />, color: "primary" },
  { action: "Approve", label: "Approve", icon: <CheckIcon />, color: "success" },
  { action: "Reject", label: "Reject", icon: <CloseIcon />, color: "error" },
  { action: "ReturnInvoiceToDraft", label: "Return to Draft", icon: <RevertIcon />, color: "secondary" },
  { action: "DeleteInvoice", label: "Delete", icon: <DeleteIcon />, color: "error" },
];

const InvoicesGridHeaderToolbar = () => {
  const { refreshObjects } = useDefinedContext(ObjectsListContext, "ObjectsListContext");
  const apiRef = useGridApiContext();
  const selectedIds = getSelectedRowIdsFromModel(useGridSelector(apiRef, gridRowSelectionStateSelector));
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const eligibility = useDefinedContext(InvoicesGridContext, "InvoicesGridContext");
  const { downloading, downloadInvoiceDocuments } = useInvoiceDocumentsDownload();

  const [actionToConfirm, setActionToConfirm] = useState<InvoiceAction>();

  const selectedInvoices = selectedIds.flatMap((id) => (rowsLookup[id] as ObjectFieldValues | undefined) ?? []);

  const handleExecuted = (succeededInvoiceIds: string[]) => {
    apiRef.current.selectRows(succeededInvoiceIds, false);
    refreshObjects();
  };

  return (
    <>
      <Button
        color="secondary"
        variant="text"
        loading={downloading}
        startIcon={<DownloadIcon />}
        onClick={() => downloadInvoiceDocuments(selectedIds)}
      >
        Download
      </Button>
      {actionButtons
        .filter(({ action }) =>
          selectedInvoices.some((invoice) => isInvoiceActionAvailable(invoice, action, eligibility))
        )
        .map(({ action, label, icon, color }) => (
          <Button key={action} color={color} variant="text" startIcon={icon} onClick={() => setActionToConfirm(action)}>
            {label}
          </Button>
        ))}
      {actionToConfirm !== undefined && (
        <InvoiceActionDialog
          action={actionToConfirm}
          invoices={selectedInvoices}
          selectedInvoiceCount={selectedIds.length}
          onClose={() => setActionToConfirm(undefined)}
          onExecuted={handleExecuted}
        />
      )}
    </>
  );
};

export default InvoicesGridHeaderToolbar;
